/**
 * The overview area's words (console redesign, docs/console-redesign-plan.md).
 *
 * One file per area so the redesign's parallel work never edits the same
 * dictionary. `en.ts` and `es.ts` spread these in; the Spanish half is typed
 * against the English one, so a key missing here is a build failure exactly as
 * it is in the main catalog.
 *
 * Neutral pronouns throughout — "they", "the owner" — because the console
 * cannot know how each owner is referred to (plan §2).
 */
export const overviewEn = {
  'overview.loading.readiness': 'where they are in getting live',
  'overview.loading.now': 'what is happening right now',
  'overview.loading.lately': 'recent changes',

  /* ---- the next step ------------------------------------------------ */
  'overview.next': ({ step }: { step: string }) => `Next: ${step}`,
  'overview.next.owner': 'Waiting on the owner.',

  /* ---- the checklist ------------------------------------------------ */
  'overview.checklist.title': 'Getting live',
  'overview.checklist.count': ({ done, total }: { done: number; total: number }) =>
    `${String(done)} of ${String(total)} done`,
  'overview.online.title': 'Taking bookings online',
  'overview.online.note':
    'The later stage. These are done on the Website and Connections tabs, once the business runs here.',
  'overview.tick.done': 'Done',
  'overview.tick.wait': 'Waiting',
  'overview.tick.open': 'Not done',
  'overview.who.operator': 'Yours to do',
  'overview.who.owner': 'Waiting on the owner',
  'overview.who.platform': 'Nothing to do — it finishes on its own',

  'overview.step.catalog.title': 'What they sell, with prices',
  'overview.step.catalog.empty': 'Nothing in the catalog yet',
  'overview.step.catalog.nextEmpty': 'put in what they sell',
  'overview.step.catalog.actionEmpty': 'Open the catalog',
  'overview.step.catalog.unpriced': ({ unpriced, total }: { unpriced: number; total: number }) =>
    `${String(unpriced)} of ${String(total)} have no price yet`,
  'overview.step.catalog.nextPrices': 'put prices on what they sell',
  'overview.step.catalog.actionPrices': 'Set prices',
  'overview.step.catalog.done': ({ services, other }: { services: number; other: number }) =>
    `${String(services)} ${services === 1 ? 'service' : 'services'}` +
    (other > 0 ? ` · ${String(other)} other ${other === 1 ? 'thing' : 'things'}` : '') +
    ' · all priced',

  'overview.step.features.title': 'The features they need',
  'overview.step.features.open': 'Only the basics are on',
  'overview.step.features.next': 'choose the features they need',
  'overview.step.features.action': 'Choose features',

  'overview.step.tenders.title': 'Payments they take at the counter',
  'overview.step.tenders.open': 'Not set — the till shows cash, card and other',
  'overview.step.tenders.next': 'set the payments they take at the counter',
  'overview.step.tenders.action': 'Set payments',
  'overview.tender.cash': 'Cash',
  'overview.tender.check': 'Check',
  'overview.tender.card': 'Card',
  'overview.tender.other': 'Other',

  'overview.step.hours.title': 'Their opening hours',
  'overview.step.hours.done': ({ days }: { days: number }) =>
    `Open ${String(days)} ${days === 1 ? 'day' : 'days'} a week`,
  'overview.step.hours.open': 'Not set — every day reads as closed',
  'overview.step.hours.next': 'put in their opening hours',
  'overview.step.hours.action': 'Set hours',

  'overview.step.clients.title': 'Their clients and appointments',
  'overview.step.clients.done': ({ count }: { count: number }) =>
    `${String(count)} ${count === 1 ? 'client' : 'clients'} in their book`,
  'overview.step.clients.wait': 'Waiting on their client list and appointment book',
  'overview.step.clients.next': 'get their client list and appointment book',
  'overview.step.clients.action': 'Import…',

  'overview.step.staff.title': 'Their staff login',
  'overview.step.staff.done': ({ count }: { count: number }) =>
    `${String(count)} staff ${count === 1 ? 'login' : 'logins'}`,
  'overview.step.staff.open':
    'Not invited yet · invite them from Setup, under Who can sign in; try one on your own address first',
  'overview.step.staff.next': 'invite their staff login',
  'overview.step.staff.action': 'Invite…',

  'overview.step.look.title': 'Their look in the staff app',
  'overview.step.look.done': 'Applied',
  'overview.step.look.open': 'Not applied — the staff app shows the platform’s own look',
  'overview.step.look.next': 'apply their look in the staff app',
  'overview.step.look.action': 'Review',

  'overview.step.switch.title': 'The switch-over with the owner',
  'overview.step.switch.done': 'They run their day here',
  'overview.step.switch.ready':
    'Everything above is done · move them to Live from the stage menu when the owner is ready',
  'overview.step.switch.gated': 'After everything above',
  'overview.step.switch.next': 'switch them over to Live',

  'overview.step.questionnaire.title': 'The owner’s questionnaire',
  'overview.step.questionnaire.none': 'No link made yet',
  'overview.step.questionnaire.nextSend': 'send the owner their questionnaire',
  'overview.step.questionnaire.actionSend': 'Make the link',
  'overview.step.questionnaire.closed': 'Answered and closed',
  'overview.step.questionnaire.answered': 'Answered · ready to review',
  'overview.step.questionnaire.nextReview': 'review the owner’s answers',
  'overview.step.questionnaire.actionReview': 'Review',
  'overview.step.questionnaire.expired': 'The link ran out before it was answered',
  'overview.step.questionnaire.nextExtend': 'give the owner a fresh questionnaire link',
  'overview.step.questionnaire.actionExtend': 'Open',
  'overview.step.questionnaire.sent': 'Sent · waiting on the owner’s answers',
  'overview.step.questionnaire.nextWait': 'get the owner’s answers to the questionnaire',
  'overview.step.questionnaire.actionOpen': 'Open',

  'overview.step.site.title': 'Their website',
  'overview.step.site.done': 'Published',
  'overview.step.site.open': 'Not published yet',
  'overview.step.site.next': 'publish their website',
  'overview.step.site.action': 'Open the website',

  'overview.step.address.title': 'Their own web address',
  'overview.step.address.done': 'Live',
  'overview.step.address.wait': 'Added · waiting for it to connect',
  'overview.step.address.nextCheck': 'check their web address',
  'overview.step.address.actionCheck': 'Check',
  'overview.step.address.open': 'Only the platform’s address so far',
  'overview.step.address.next': 'put their own address on the website',
  'overview.step.address.action': 'Add an address',

  'overview.step.texts.title': 'Texts to their clients',
  'overview.step.texts.done': 'Appointment reminders are on',
  'overview.step.texts.partner': 'Their booking partner sends them',
  'overview.step.texts.open': 'Reminders are off · they are switched on from the operator tool',
  'overview.step.texts.next': 'turn on appointment reminders',

  'overview.step.concierge.title': 'The concierge',
  'overview.step.concierge.done': 'Connected',
  'overview.step.concierge.open': 'Not connected',
  'overview.step.concierge.next': 'connect the concierge',
  'overview.step.concierge.action': 'Connect',

  /* ---- right now ---------------------------------------------------- */
  'overview.now.title': 'Right now',
  'overview.verdict.quiet': 'Nothing wrong',
  'overview.verdict.attention': 'Worth a look',
  'overview.verdict.failing': 'Something gave up',
  'overview.refreshed': 'Refreshed',
  'overview.reread': 'Re-read',

  'overview.signal.book': 'Their book',
  'overview.signal.book.none': 'No bookings yet',
  'overview.signal.book.week': ({ count }: { count: number }) =>
    `${String(count)} in the next 7 days`,
  'overview.signal.book.last': ({ when }: { when: string }) => `last booked ${when}`,
  'overview.signal.book.never': 'never booked',
  'overview.signal.texts': 'Texts to clients',
  'overview.signal.texts.off': 'Not set up',
  'overview.signal.texts.offNote': 'comes with online bookings',
  'overview.signal.texts.sent': ({ count }: { count: number }) =>
    `${String(count)} sent in the last day`,
  'overview.signal.texts.failed': ({ count }: { count: number }) =>
    count === 0 ? 'none failed' : `${String(count)} failed`,
  'overview.signal.requests': 'Requests waiting',
  'overview.signal.requests.none': 'None',
  'overview.signal.requests.waiting': ({ count }: { count: number }) =>
    `${String(count)} waiting on the owner`,
  'overview.signal.requests.overdue': ({ count }: { count: number }) =>
    `${String(count)} past their hold`,
  'overview.signal.site': 'Their website',
  'overview.signal.site.default': 'On the platform’s address',
  'overview.signal.site.up': 'Up',
  'overview.signal.site.notUp': 'Not reachable yet',

  'overview.reason.noKey':
    'The concierge is connected but holds no access key, so it cannot reach them.',
  'overview.reason.unknown': 'Something the console does not have words for yet needs a look.',
  'overview.fix.connections': 'Open Connections',
  'overview.fix.site': 'Check the address',
  'overview.fix.texts':
    'Texts retry on their own; if this stays, the platform’s texting needs a look.',
  'overview.fix.requests': 'The owner answers requests in the staff app.',

  'overview.details': 'Details',
  'overview.details.site': 'Website',
  'overview.details.site.default': 'Uses the platform’s address, so there is nothing to check.',
  'overview.details.site.reachable': 'reachable',
  'overview.details.site.unreachable': 'not reachable',
  'overview.details.site.verified': 'certificate checked',
  'overview.details.site.changed': 'last changed',
  'overview.details.site.note':
    'Read from the platform’s own records, not by visiting the site — a visit from inside the platform would not prove a guest can reach it.',
  'overview.domain.pending_dns': 'waiting for its domain settings',
  'overview.domain.issuing': 'getting its certificate',
  'overview.domain.live': 'live',
  'overview.details.texts': 'Texts',
  'overview.details.updates': 'Updates to partners',
  'overview.details.queue': ({
    hours,
    sent,
    queued,
    failed,
    exhausted,
  }: {
    hours: number;
    sent: number;
    queued: number;
    failed: number;
    exhausted: number;
  }) =>
    `Last ${String(hours)} hours: ${String(sent)} sent · ${String(queued)} waiting · ${String(failed)} failed and retrying · ${String(exhausted)} gave up`,
  'overview.details.oldestWaiting': 'oldest still waiting',
  'overview.details.endpoints': ({ count }: { count: number }) =>
    `${String(count)} partner ${count === 1 ? 'address' : 'addresses'} receiving updates`,
  'overview.details.endpointFailing': 'one is failing',
  'overview.details.newestGaveUp': 'last gave up',
  'overview.details.requests': 'Requests',
  'overview.details.requests.counts': ({
    waiting,
    overdue,
  }: {
    waiting: number;
    overdue: number;
  }) => `${String(waiting)} waiting on the owner · ${String(overdue)} past their hold`,
  'overview.details.requests.oldest': 'oldest past its hold',
  'overview.details.book': 'Book',
  'overview.details.book.week': ({ count }: { count: number }) =>
    `${String(count)} confirmed in the next 7 days`,
  'overview.details.book.last': 'last booking',
  'overview.details.book.never': 'no booking yet',
  'overview.details.key': 'Partner access key',
  'overview.details.key.none': 'None — no partner can reach them',
  'overview.details.key.lastUsed': 'last used',
  'overview.details.key.never': 'not used yet',
  'overview.details.key.batch':
    'Last use is recorded in batches, so it can trail the real moment a little.',

  /* ---- lately ------------------------------------------------------- */
  'overview.lately.title': 'Lately',
  'overview.lately.all': 'All activity →',
  'overview.lately.empty': 'No changes yet.',
};

export const overviewEs: typeof overviewEn = {
  'overview.loading.readiness': 'en qué punto están',
  'overview.loading.now': 'lo que pasa ahora mismo',
  'overview.loading.lately': 'los cambios recientes',

  'overview.next': ({ step }) => `Siguiente: ${step}`,
  'overview.next.owner': 'Esto depende del propietario.',

  'overview.checklist.title': 'Camino a estar en marcha',
  'overview.checklist.count': ({ done, total }) => `${String(done)} de ${String(total)} hechos`,
  'overview.online.title': 'Reservas en línea',
  'overview.online.note':
    'La etapa siguiente. Se hace en las pestañas Sitio web y Conexiones, cuando el negocio ya funcione aquí.',
  'overview.tick.done': 'Hecho',
  'overview.tick.wait': 'En espera',
  'overview.tick.open': 'Pendiente',
  'overview.who.operator': 'Te toca a ti',
  'overview.who.owner': 'Depende del propietario',
  'overview.who.platform': 'No hay nada que hacer — termina solo',

  'overview.step.catalog.title': 'Lo que venden, con precios',
  'overview.step.catalog.empty': 'El catálogo está vacío',
  'overview.step.catalog.nextEmpty': 'cargar lo que venden',
  'overview.step.catalog.actionEmpty': 'Abrir el catálogo',
  'overview.step.catalog.unpriced': ({ unpriced, total }) =>
    `${String(unpriced)} de ${String(total)} aún no tienen precio`,
  'overview.step.catalog.nextPrices': 'poner precio a lo que venden',
  'overview.step.catalog.actionPrices': 'Poner precios',
  'overview.step.catalog.done': ({ services, other }) =>
    `${String(services)} ${services === 1 ? 'servicio' : 'servicios'}` +
    (other > 0 ? ` · ${String(other)} ${other === 1 ? 'cosa más' : 'cosas más'}` : '') +
    ' · todo con precio',

  'overview.step.features.title': 'Las funciones que necesitan',
  'overview.step.features.open': 'Solo está lo básico',
  'overview.step.features.next': 'elegir las funciones que necesitan',
  'overview.step.features.action': 'Elegir funciones',

  'overview.step.tenders.title': 'Cómo les pagan en el mostrador',
  'overview.step.tenders.open': 'Sin configurar — la caja muestra efectivo, tarjeta y otro',
  'overview.step.tenders.next': 'configurar cómo les pagan en el mostrador',
  'overview.step.tenders.action': 'Configurar pagos',
  'overview.tender.cash': 'Efectivo',
  'overview.tender.check': 'Cheque',
  'overview.tender.card': 'Tarjeta',
  'overview.tender.other': 'Otro',

  'overview.step.hours.title': 'Su horario',
  'overview.step.hours.done': ({ days }) =>
    `Abierto ${String(days)} ${days === 1 ? 'día' : 'días'} a la semana`,
  'overview.step.hours.open': 'Sin horario — todos los días figuran cerrados',
  'overview.step.hours.next': 'cargar su horario',
  'overview.step.hours.action': 'Poner horario',

  'overview.step.clients.title': 'Sus clientes y citas',
  'overview.step.clients.done': ({ count }) =>
    `${String(count)} ${count === 1 ? 'cliente' : 'clientes'} en su agenda`,
  'overview.step.clients.wait': 'Esperando su lista de clientes y su libro de citas',
  'overview.step.clients.next': 'conseguir su lista de clientes y su libro de citas',
  'overview.step.clients.action': 'Importar…',

  'overview.step.staff.title': 'Su acceso para el personal',
  'overview.step.staff.done': ({ count }) =>
    `${String(count)} ${count === 1 ? 'acceso' : 'accesos'} del personal`,
  'overview.step.staff.open':
    'Sin invitar · invítelos desde Configuración, en Quién puede entrar; pruebe primero con su propia dirección',
  'overview.step.staff.next': 'invitar su acceso para el personal',
  'overview.step.staff.action': 'Invitar…',

  'overview.step.look.title': 'Su imagen en la app del personal',
  'overview.step.look.done': 'Aplicada',
  'overview.step.look.open': 'Sin aplicar — la app del personal muestra la imagen de la plataforma',
  'overview.step.look.next': 'aplicar su imagen en la app del personal',
  'overview.step.look.action': 'Revisar',

  'overview.step.switch.title': 'El cambio con el propietario',
  'overview.step.switch.done': 'Ya llevan su día aquí',
  'overview.step.switch.ready':
    'Todo lo anterior está hecho · pásalos a En marcha desde el menú de etapas cuando el propietario esté listo',
  'overview.step.switch.gated': 'Después de todo lo anterior',
  'overview.step.switch.next': 'pasarlos a En marcha',

  'overview.step.questionnaire.title': 'El cuestionario del propietario',
  'overview.step.questionnaire.none': 'Aún no hay enlace',
  'overview.step.questionnaire.nextSend': 'enviar al propietario su cuestionario',
  'overview.step.questionnaire.actionSend': 'Crear el enlace',
  'overview.step.questionnaire.closed': 'Respondido y cerrado',
  'overview.step.questionnaire.answered': 'Respondido · listo para revisar',
  'overview.step.questionnaire.nextReview': 'revisar las respuestas del propietario',
  'overview.step.questionnaire.actionReview': 'Revisar',
  'overview.step.questionnaire.expired': 'El enlace venció sin respuesta',
  'overview.step.questionnaire.nextExtend': 'dar al propietario un enlace nuevo',
  'overview.step.questionnaire.actionExtend': 'Abrir',
  'overview.step.questionnaire.sent': 'Enviado · esperando las respuestas del propietario',
  'overview.step.questionnaire.nextWait': 'conseguir las respuestas del propietario',
  'overview.step.questionnaire.actionOpen': 'Abrir',

  'overview.step.site.title': 'Su sitio web',
  'overview.step.site.done': 'Publicado',
  'overview.step.site.open': 'Aún sin publicar',
  'overview.step.site.next': 'publicar su sitio web',
  'overview.step.site.action': 'Abrir el sitio web',

  'overview.step.address.title': 'Su propia dirección web',
  'overview.step.address.done': 'Activa',
  'overview.step.address.wait': 'Añadida · esperando a que conecte',
  'overview.step.address.nextCheck': 'revisar su dirección web',
  'overview.step.address.actionCheck': 'Revisar',
  'overview.step.address.open': 'Por ahora solo la dirección de la plataforma',
  'overview.step.address.next': 'poner su propia dirección en el sitio',
  'overview.step.address.action': 'Añadir una dirección',

  'overview.step.texts.title': 'Mensajes a sus clientes',
  'overview.step.texts.done': 'Los recordatorios de cita están activados',
  'overview.step.texts.partner': 'Los envía su socio de reservas',
  'overview.step.texts.open':
    'Recordatorios desactivados · se activan desde la herramienta del operador',
  'overview.step.texts.next': 'activar los recordatorios de cita',

  'overview.step.concierge.title': 'El conserje',
  'overview.step.concierge.done': 'Conectado',
  'overview.step.concierge.open': 'Sin conectar',
  'overview.step.concierge.next': 'conectar el conserje',
  'overview.step.concierge.action': 'Conectar',

  'overview.now.title': 'Ahora mismo',
  'overview.verdict.quiet': 'Todo en orden',
  'overview.verdict.attention': 'Conviene mirar',
  'overview.verdict.failing': 'Algo se rindió',
  'overview.refreshed': 'Actualizado',
  'overview.reread': 'Volver a leer',

  'overview.signal.book': 'Su agenda',
  'overview.signal.book.none': 'Aún sin reservas',
  'overview.signal.book.week': ({ count }) => `${String(count)} en los próximos 7 días`,
  'overview.signal.book.last': ({ when }) => `última reserva ${when}`,
  'overview.signal.book.never': 'nunca ha reservado nadie',
  'overview.signal.texts': 'Mensajes a clientes',
  'overview.signal.texts.off': 'Sin configurar',
  'overview.signal.texts.offNote': 'llega con las reservas en línea',
  'overview.signal.texts.sent': ({ count }) => `${String(count)} enviados en el último día`,
  'overview.signal.texts.failed': ({ count }) =>
    count === 0 ? 'ninguno falló' : `${String(count)} fallaron`,
  'overview.signal.requests': 'Solicitudes pendientes',
  'overview.signal.requests.none': 'Ninguna',
  'overview.signal.requests.waiting': ({ count }) => `${String(count)} esperando al propietario`,
  'overview.signal.requests.overdue': ({ count }) => `${String(count)} pasadas de plazo`,
  'overview.signal.site': 'Su sitio web',
  'overview.signal.site.default': 'En la dirección de la plataforma',
  'overview.signal.site.up': 'Funcionando',
  'overview.signal.site.notUp': 'Aún no se puede visitar',

  'overview.reason.noKey':
    'El conserje está conectado pero no tiene clave de acceso, así que no puede llegar a ellos.',
  'overview.reason.unknown':
    'Algo para lo que la consola aún no tiene palabras necesita una mirada.',
  'overview.fix.connections': 'Abrir Conexiones',
  'overview.fix.site': 'Revisar la dirección',
  'overview.fix.texts':
    'Los mensajes se reintentan solos; si esto sigue, el envío de mensajes de la plataforma necesita una mirada.',
  'overview.fix.requests': 'El propietario responde las solicitudes en la app del personal.',

  'overview.details': 'Detalles',
  'overview.details.site': 'Sitio web',
  'overview.details.site.default':
    'Usa la dirección de la plataforma, así que no hay nada que revisar.',
  'overview.details.site.reachable': 'se puede visitar',
  'overview.details.site.unreachable': 'no se puede visitar',
  'overview.details.site.verified': 'certificado comprobado',
  'overview.details.site.changed': 'último cambio',
  'overview.details.site.note':
    'Se lee de los registros de la plataforma, no visitando el sitio — una visita desde dentro de la plataforma no probaría que un cliente puede llegar.',
  'overview.domain.pending_dns': 'esperando la configuración de su dominio',
  'overview.domain.issuing': 'obteniendo su certificado',
  'overview.domain.live': 'activa',
  'overview.details.texts': 'Mensajes',
  'overview.details.updates': 'Avisos a socios',
  'overview.details.queue': ({ hours, sent, queued, failed, exhausted }) =>
    `Últimas ${String(hours)} horas: ${String(sent)} enviados · ${String(queued)} en espera · ${String(failed)} fallaron y se reintentan · ${String(exhausted)} se rindieron`,
  'overview.details.oldestWaiting': 'el más antiguo aún en espera',
  'overview.details.endpoints': ({ count }) =>
    `${String(count)} ${count === 1 ? 'dirección' : 'direcciones'} de socios recibiendo avisos`,
  'overview.details.endpointFailing': 'una está fallando',
  'overview.details.newestGaveUp': 'la última vez que se rindió',
  'overview.details.requests': 'Solicitudes',
  'overview.details.requests.counts': ({ waiting, overdue }) =>
    `${String(waiting)} esperando al propietario · ${String(overdue)} pasadas de plazo`,
  'overview.details.requests.oldest': 'la más antigua pasada de plazo',
  'overview.details.book': 'Agenda',
  'overview.details.book.week': ({ count }) =>
    `${String(count)} confirmadas en los próximos 7 días`,
  'overview.details.book.last': 'última reserva',
  'overview.details.book.never': 'aún sin reservas',
  'overview.details.key': 'Clave de acceso de socios',
  'overview.details.key.none': 'Ninguna — ningún socio puede llegar a ellos',
  'overview.details.key.lastUsed': 'último uso',
  'overview.details.key.never': 'aún sin usar',
  'overview.details.key.batch':
    'El último uso se registra por tandas, así que puede ir un poco por detrás del momento real.',

  'overview.lately.title': 'Últimamente',
  'overview.lately.all': 'Toda la actividad →',
  'overview.lately.empty': 'Aún no hay cambios.',
};
